// Node globals for browser — must run before any @solana/* import.
// web3.js + anchor + wallet-adapter all read Buffer at module-eval time.
import { Buffer } from "buffer";
if (typeof globalThis.Buffer === "undefined") {
  globalThis.Buffer = Buffer;
}

import React, { useMemo } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import {
  ConnectionProvider,
  WalletProvider,
} from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import {
  SolanaMobileWalletAdapter,
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
} from "@solana-mobile/wallet-adapter-mobile";
import type { Adapter } from "@solana/wallet-adapter-base";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";

import "@solana/wallet-adapter-react-ui/styles.css";

import { App } from "./App";
import { installErrorCapture } from "./dev/store/devErrorLog";
import { IdentityProvider } from "./identity";
import { resolveBrand } from "./launch/brand";
import { LiveKitProvider } from "./voice/LiveKitProvider";

// Capture console.error + unhandledrejection into the dev console's error
// log. No-op outside DEV / `?debug=1`, so prod stream viewers don't see it.
installErrorCapture();

// Boot-time brand (from ?launch=, default Crownclash). Drives both the wallet
// popup metadata below and the page chrome. Wallets read document.title +
// favicon + origin for their connect popup — that's the only handle a dapp has
// on what that popup says, so set them here as early as possible.
const brand = resolveBrand();
if (typeof document !== "undefined") {
  document.title = `${brand.appName} — Last Touch`;
  const link =
    document.querySelector<HTMLLinkElement>('link[rel="icon"]') ??
    document.head.appendChild(
      Object.assign(document.createElement("link"), { rel: "icon" }),
    );
  link.href = brand.iconUrl;
}

/**
 * App bootstrap:
 * - Wallet adapter context wired to local surfpool simnet by default
 *   (override with VITE_SOLANA_RPC, e.g. devnet/mainnet for production).
 * - IdentityProvider comes from Lane D (./identity). We deliberately do not
 *   ship a stub file here, per the lane brief — see commit message.
 * - BrowserRouter wraps App so route components can lazy-load arena/explorer.
 */
function Root(): JSX.Element {
  const endpoint =
    (import.meta.env.VITE_SOLANA_RPC as string | undefined) ??
    "http://127.0.0.1:8899";

  const wallets = useMemo<Adapter[]>(
    () => [
      // MWA: active on Android Chrome. Stays dormant on desktop and iOS, so it's
      // safe to register unconditionally. iOS Safari is not supported by MWA at
      // all (per docs.solanamobile.com) — for that path App.tsx renders the
      // <IosWalletDeeplinkBanner /> (from ./wallet/) to push users into
      // Phantom's in-app browser, where the wallet-standard auto-discovery
      // picks up Phantom via window injection. Desktop Phantom/Solflare are
      // also auto-registered as Standard Wallets — no explicit adapter
      // entries needed.
      new SolanaMobileWalletAdapter({
        addressSelector: createDefaultAddressSelector(),
        appIdentity: {
          name: brand.appName,
          uri: typeof window !== "undefined" ? window.location.origin : brand.url,
          icon: brand.iconUrl,
        },
        authorizationResultCache: createDefaultAuthorizationResultCache(),
        cluster:
          (import.meta.env.VITE_SOLANA_CLUSTER as
            | WalletAdapterNetwork
            | undefined) ?? WalletAdapterNetwork.Devnet,
        onWalletNotFound: createDefaultWalletNotFoundHandler(),
      }),
    ],
    [],
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      {/* autoConnect reconnects to the last-used wallet on refresh (the
          adapter remembers it in localStorage), so a reload doesn't read as a
          logout. Wallets only reconnect silently when the user previously
          approved this site — otherwise it's a no-op, no surprise popup. */}
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <IdentityProvider>
            <LiveKitProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </LiveKitProvider>
          </IdentityProvider>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("root element missing in index.html");
}
createRoot(container).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
